import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Sidebar } from '@/components/Sidebar';
import { PatientHistoryDialog } from '@/components/PatientHistoryDialog';
import { PatientConsentDialog } from '@/components/PatientConsentDialog';
import { Plus, Search, Pencil, Trash2, Phone, Users, History, ArrowDownCircle, ArrowUpCircle, Tag, Paperclip, ArrowUp, ArrowDown, X, Type, List, Calendar, Hash, LayoutDashboard, Bell, MoreHorizontal, Columns3, ListFilter, ShieldCheck, ShieldAlert } from 'lucide-react';
import { useIndustry } from '@/components/IndustryContext';
import { formatMoney } from '@/lib/currency';

const MAX_CUSTOM_COLUMNS = 24;
const MAX_COLUMN_NAME_LENGTH = 48;
const MAX_SELECT_OPTIONS = 30;
const MAX_SELECT_OPTION_LENGTH = 60;
const MAX_PATIENT_TOPICS = 12;
const MAX_PATIENT_TOPIC_LENGTH = 48;
const PATIENT_TABLE_WIDTHS_STORAGE_KEY = 'pp_patient_table_column_widths_v1';
const PATIENT_TABLE_COLUMN_CONFIG = {
  name: { defaultWidth: 220, minWidth: 120, maxWidth: 480 },
  topics: { defaultWidth: 220, minWidth: 100, maxWidth: 480 },
  phone: { defaultWidth: 170, minWidth: 110, maxWidth: 420 },
  balance: { defaultWidth: 110, minWidth: 84, maxWidth: 280 },
  actions: { defaultWidth: 108, minWidth: 108, maxWidth: 108 },
  custom: { defaultWidth: 180, minWidth: 96, maxWidth: 560 },
};

const normalizeKey = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru-RU');
const customColumnWidthKey = (name) => `custom:${normalizeKey(name)}`;
const getPatientColumnConfig = (key) => key.startsWith('custom:')
  ? PATIENT_TABLE_COLUMN_CONFIG.custom
  : (PATIENT_TABLE_COLUMN_CONFIG[key] || PATIENT_TABLE_COLUMN_CONFIG.custom);
const clampPatientColumnWidth = (key, value) => {
  const { defaultWidth, minWidth, maxWidth } = getPatientColumnConfig(key);
  const numericValue = Number(value);
  return Math.min(maxWidth, Math.max(minWidth, Number.isFinite(numericValue) ? numericValue : defaultWidth));
};
const readStoredPatientColumnWidths = () => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = JSON.parse(window.localStorage.getItem(PATIENT_TABLE_WIDTHS_STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(Object.entries(stored).map(([key, value]) => [key, clampPatientColumnWidth(key, value)]));
  } catch {
    return {};
  }
};
const cleanColumnName = (value) => String(value || '').trim();
const cleanOptions = (value) => {
  const seen = new Set();
  return String(value || '').split(',').map(cleanColumnName).filter(option => {
    const key = normalizeKey(option);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeTopics = (values) => {
  const seen = new Set();
  return (Array.isArray(values) ? values : []).map(value => String(value || '').trim().replace(/\s+/g, ' ')).filter(topic => {
    const key = normalizeKey(topic);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getPatientTopics = (patient) => {
  const legacyTopics = Object.entries(patient?.custom_fields || {})
    .filter(([key]) => ['тема', 'темы', 'topic', 'topics'].includes(normalizeKey(key)))
    .flatMap(([, value]) => String(value || '').split(/[,;]/));
  return normalizeTopics([...(patient?.topics || []), ...legacyTopics]);
};

// Normalize column: old format (string) → new format (object)
const normalizeCol = (col) => {
  if (typeof col === 'string') return { name: cleanColumnName(col), type: 'text', options: [], visible_to: [] };
  if (!col || typeof col !== 'object') return null;
  return {
    name: cleanColumnName(col.name),
    type: col.type || 'text',
    options: Array.isArray(col.options) ? col.options : [],
    visible_to: Array.isArray(col.visible_to) ? col.visible_to : [],
  };
};

const COL_TYPES = [
  { id: 'text', label: 'Текст', icon: Type },
  { id: 'select', label: 'Выбор из списка', icon: List },
  { id: 'date', label: 'Дата', icon: Calendar },
  { id: 'number', label: 'Число', icon: Hash },
];

export default function PatientsPage() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '', topics: [] });
  const [topicInput, setTopicInput] = useState('');
  const [rankTopic, setRankTopic] = useState('');
  const [customFieldsForm, setCustomFieldsForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyPatient, setHistoryPatient] = useState(null);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [balancePatient, setBalancePatient] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceType, setBalanceType] = useState('deposit');
  const [pricesOpen, setPricesOpen] = useState(false);
  const [pricesPatient, setPricesPatient] = useState(null);
  const [customPrices, setCustomPrices] = useState({});
  const [formats, setFormats] = useState([]);
  const [currency, setCurrency] = useState('RUB');
  const [rawColumns, setRawColumns] = useState([]);
  const [columnWidths, setColumnWidths] = useState(readStoredPatientColumnWidths);
  const [resizingColumn, setResizingColumn] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  // Add column
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColType, setNewColType] = useState('text');
  const [newColOptions, setNewColOptions] = useState('');
  const [newColVisibility, setNewColVisibility] = useState(['doctor', 'admin']);
  // Edit column (change type/options)
  const [editColOpen, setEditColOpen] = useState(false);
  const [editCol, setEditCol] = useState(null);
  const [columnToDelete, setColumnToDelete] = useState(null);
  const [patientToDelete, setPatientToDelete] = useState(null);
  const [uploadPatient, setUploadPatient] = useState(null);
  const fileInputRef = useRef(null);
  const deepLinkedClientRef = useRef('');
  const columnResizeRef = useRef(null);
  // Inline editing
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  // Notification
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyPatient, setNotifyPatient] = useState(null);
  const [notifyForm, setNotifyForm] = useState({ subject: '', message: '' });
  const [userRole, setUserRole] = useState('doctor');
  const [ownerId, setOwnerId] = useState('');
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentPatient, setConsentPatient] = useState(null);
  const [consentGate, setConsentGate] = useState(false);
  const [consentContourEnabled, setConsentContourEnabled] = useState(false);
  const navigate = useNavigate();

  const { t } = useIndustry();

  const columns = useMemo(() => {
    const seen = new Set();
    return rawColumns.map(normalizeCol).filter(col => {
      const key = normalizeKey(col?.name);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawColumns]);
  const isOwner = userRole === 'owner';
  const isSoloDoctor = userRole === 'doctor' && !ownerId;
  const canManageColumns = isOwner || isSoloDoctor;
  const isOrg = isOwner || (userRole === 'admin' && !!ownerId);
  const defaultVisibility = isOrg ? ['owner', 'admin', 'doctor'] : ['doctor', 'admin'];
  // Visibility options depend on context
  const visibilityRoles = isOrg
    ? [{id: 'owner', label: 'Владелец'}, {id: 'admin', label: 'Админ'}, {id: 'doctor', label: 'Специалист'}]
    : [{id: 'doctor', label: 'Специалист'}, {id: 'admin', label: 'Администратор'}];
  // Filter columns by role visibility
  const visibleColumns = useMemo(() => columns.filter(col => {
    const vt = col.visible_to;
    if (!vt || !Array.isArray(vt) || vt.length === 0) return true; // backward compat: show to all
    return vt.includes(userRole);
  }), [columns, userRole]);

  const getColumnWidth = useCallback((key) => {
    const config = getPatientColumnConfig(key);
    return clampPatientColumnWidth(key, columnWidths[key] ?? config.defaultWidth);
  }, [columnWidths]);

  const patientTableMinWidth = useMemo(() => (
    ['name', 'topics', 'phone', 'balance'].reduce((total, key) => total + getColumnWidth(key), 0)
    + visibleColumns.reduce((total, col) => total + getColumnWidth(customColumnWidthKey(col.name)), 0)
    + PATIENT_TABLE_COLUMN_CONFIG.actions.defaultWidth
  ), [getColumnWidth, visibleColumns]);

  const topicStats = useMemo(() => {
    const stats = new Map();
    patients.forEach(patient => {
      getPatientTopics(patient).forEach(topic => {
        const key = normalizeKey(topic);
        const current = stats.get(key) || { name: topic, count: 0 };
        current.count += 1;
        stats.set(key, current);
      });
    });
    return [...stats.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'ru'));
  }, [patients]);

  const rankedPatientCount = useMemo(() => {
    if (!rankTopic) return 0;
    const rankKey = normalizeKey(rankTopic);
    return patients.filter(patient => getPatientTopics(patient).some(topic => normalizeKey(topic) === rankKey)).length;
  }, [patients, rankTopic]);

  const apiErrorMessage = (err, fallback) => {
    const detail = err.response?.data?.detail;
    return typeof detail === 'string' ? detail : detail?.message || fallback;
  };

  const openConsent = useCallback((patient, gate = false) => {
    if (!consentContourEnabled) return;
    setConsentPatient(patient);
    setConsentGate(gate);
    setConsentOpen(true);
  }, [consentContourEnabled]);

  const saveColumns = async (updated) => {
    // Guard: don't save if we haven't loaded columns yet (prevent accidental wipe)
    if (!updated && rawColumns.length === 0) return false;
    try {
      await api.put('/settings', { custom_patient_columns: updated });
      setRawColumns(updated);
      return true;
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Ошибка сохранения столбцов'));
      return false;
    }
  };

  const fetchPatients = useCallback(async () => {
    try {
      const [pRes, sRes, consentFlagsRes] = await Promise.all([
        api.get('/patients'),
        api.get('/settings'),
        api.get('/consents/feature-flags'),
      ]);
      setPatients(pRes.data);
      setFormats(sRes.data.meeting_formats || []);
      setCurrency(sRes.data.currency || 'RUB');
      setRawColumns(sRes.data.custom_patient_columns || []);
      setConsentContourEnabled(Boolean(consentFlagsRes.data?.feature_flags?.sensitive_data_consent_gate));
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    fetchPatients();
    api.get('/auth/me').then(({ data }) => {
      setUserRole(data.role || 'doctor');
      setOwnerId(data.owner_id || '');
    }).catch(() => {});
  }, [navigate, fetchPatients]);

  useEffect(() => {
    const clientId = new URLSearchParams(window.location.search).get('client') || '';
    if (!consentContourEnabled || !clientId || deepLinkedClientRef.current === clientId) return;
    const patient = patients.find(item => item.id === clientId);
    if (!patient) return;
    deepLinkedClientRef.current = clientId;
    openConsent(patient, patient.sensitive_sections_locked);
  }, [patients, consentContourEnabled, openConsent]);

  useEffect(() => {
    if (rankTopic && !topicStats.some(topic => normalizeKey(topic.name) === normalizeKey(rankTopic))) {
      setRankTopic('');
    }
  }, [rankTopic, topicStats]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PATIENT_TABLE_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths));
    } catch {
      // The table remains resizable even when browser storage is unavailable.
    }
  }, [columnWidths]);

  useEffect(() => {
    const finishResize = (event) => {
      const activeResize = columnResizeRef.current;
      if (!activeResize || (event?.pointerId !== undefined && event.pointerId !== activeResize.pointerId)) return;
      document.body.style.cursor = activeResize.previousCursor;
      document.body.style.userSelect = activeResize.previousUserSelect;
      columnResizeRef.current = null;
      setResizingColumn('');
    };
    const handlePointerMove = (event) => {
      const activeResize = columnResizeRef.current;
      if (!activeResize || event.pointerId !== activeResize.pointerId) return;
      const nextWidth = clampPatientColumnWidth(
        activeResize.key,
        activeResize.startWidth + event.clientX - activeResize.startX,
      );
      setColumnWidths(current => current[activeResize.key] === nextWidth
        ? current
        : { ...current, [activeResize.key]: nextWidth });
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', finishResize);
    window.addEventListener('pointercancel', finishResize);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', finishResize);
      window.removeEventListener('pointercancel', finishResize);
      finishResize();
    };
  }, []);

  const startColumnResize = (event, key) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    columnResizeRef.current = {
      key,
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: getColumnWidth(key),
      previousCursor: document.body.style.cursor,
      previousUserSelect: document.body.style.userSelect,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    setResizingColumn(key);
  };

  const resizeColumnWithKeyboard = (event, key) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const step = event.shiftKey ? 32 : 16;
    setColumnWidths(current => {
      const config = getPatientColumnConfig(key);
      const currentWidth = clampPatientColumnWidth(key, current[key] ?? config.defaultWidth);
      return { ...current, [key]: clampPatientColumnWidth(key, currentWidth + direction * step) };
    });
  };

  const resetColumnWidth = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    setColumnWidths(current => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const columnStyle = (key) => {
    const width = getColumnWidth(key);
    return { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` };
  };

  const renderColumnResizeHandle = (key, label) => {
    const config = getPatientColumnConfig(key);
    const width = getColumnWidth(key);
    const active = resizingColumn === key;
    return (
      <span role="separator" aria-orientation="vertical" aria-label={`Изменить ширину столбца «${label}»`}
        aria-valuemin={config.minWidth} aria-valuemax={config.maxWidth} aria-valuenow={width} tabIndex={0}
        data-testid={`patient-column-resizer-${key}`}
        title="Потяните, чтобы изменить ширину. Двойной щелчок — сбросить"
        onPointerDown={(event) => startColumnResize(event, key)}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => resetColumnWidth(event, key)}
        onKeyDown={(event) => resizeColumnWithKeyboard(event, key)}
        className="group/resize absolute -right-1 top-0 z-30 flex h-full w-3 touch-none cursor-col-resize items-center justify-center focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-amber-500">
        <span aria-hidden="true" className={`h-6 w-px transition-colors ${active ? 'bg-amber-500' : 'bg-stone-700 group-hover/resize:bg-amber-500 group-focus-visible/resize:bg-amber-500'}`} />
      </span>
    );
  };

  // Sorting
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null;
    return sortDir === 'asc' ? <ArrowUp size={11} className="inline ml-0.5" /> : <ArrowDown size={11} className="inline ml-0.5" />;
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru-RU');
    let list = patients.filter(p =>
      !query ||
      String(p.name || '').toLocaleLowerCase('ru-RU').includes(query) ||
      String(p.phone || '').includes(search.trim()) ||
      String(p.email || '').toLocaleLowerCase('ru-RU').includes(query) ||
      getPatientTopics(p).some(topic => topic.toLocaleLowerCase('ru-RU').includes(query)) ||
      Object.values(p.custom_fields || {}).some(v => String(v).toLocaleLowerCase('ru-RU').includes(query))
    );
    list.sort((a, b) => {
      if (rankTopic) {
        const rankKey = normalizeKey(rankTopic);
        const aMatches = getPatientTopics(a).some(topic => normalizeKey(topic) === rankKey);
        const bMatches = getPatientTopics(b).some(topic => normalizeKey(topic) === rankKey);
        if (aMatches !== bMatches) return aMatches ? -1 : 1;
      }
      let va, vb;
      if (['name', 'phone', 'email', 'notes'].includes(sortKey)) {
        va = (a[sortKey] || '').toLowerCase();
        vb = (b[sortKey] || '').toLowerCase();
      } else if (sortKey === 'balance') {
        return sortDir === 'asc' ? (a.balance || 0) - (b.balance || 0) : (b.balance || 0) - (a.balance || 0);
      } else {
        const col = columns.find(c => c.name === sortKey);
        va = (a.custom_fields || {})[sortKey] || '';
        vb = (b.custom_fields || {})[sortKey] || '';
        if (col?.type === 'number') {
          return sortDir === 'asc' ? (Number(va) || 0) - (Number(vb) || 0) : (Number(vb) || 0) - (Number(va) || 0);
        }
        if (col?.type === 'date') {
          va = va || '0000-00-00'; vb = vb || '0000-00-00';
        } else {
          va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
        }
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [patients, search, sortKey, sortDir, columns, rankTopic]);

  // CRUD
  const openAdd = () => {
    setEditId(null);
    setForm({ name: '', phone: '', email: '', notes: '', topics: [] });
    setTopicInput('');
    setCustomFieldsForm({});
    setDialogOpen(true);
  };
  const openEdit = async (p) => {
    setEditId(p.id);
    setForm({ name: p.name, phone: p.phone, email: p.email || '', notes: '', topics: normalizeTopics(p.topics || []) });
    setTopicInput('');
    setCustomFieldsForm(p.custom_fields || {});
    setDialogOpen(true);
    try {
      const { data } = await api.get(`/patients/${p.id}/notes`);
      setForm(current => ({ ...current, notes: data.notes || '' }));
    } catch (error) {
      if (error.response?.status !== 428) {
        toast.error(apiErrorMessage(error, 'Не удалось загрузить профессиональные заметки'));
      }
    }
  };
  const savePatient = async () => {
    if (!form.name.trim()) { toast.error('Введите имя клиента'); return; }
    const pendingTopics = topicInput.split(/[,;]/).map(topic => topic.trim().replace(/\s+/g, ' ')).filter(Boolean);
    if (pendingTopics.some(topic => topic.length > MAX_PATIENT_TOPIC_LENGTH)) { toast.error(`Тема: не более ${MAX_PATIENT_TOPIC_LENGTH} символов`); return; }
    const topics = normalizeTopics([...(form.topics || []), ...pendingTopics]);
    if (topics.length > MAX_PATIENT_TOPICS) { toast.error(`У клиента может быть не более ${MAX_PATIENT_TOPICS} тем`); return; }
    setSaving(true);
    try {
      const payload = { ...form, topics, custom_fields: customFieldsForm };
      const editingPatient = patients.find(patient => patient.id === editId);
      if (!editId || editingPatient?.sensitive_sections_locked) delete payload.notes;
      if (editId) { await api.put(`/patients/${editId}`, payload); toast.success('Клиент обновлён'); }
      else { await api.post('/patients', payload); toast.success('Клиент добавлен'); }
      setDialogOpen(false); fetchPatients();
    } catch (err) { toast.error(apiErrorMessage(err, 'Ошибка сохранения')); }
    setSaving(false);
  };
  const addTopicsFromInput = () => {
    const additions = topicInput.split(/[,;]/).map(topic => topic.trim().replace(/\s+/g, ' ')).filter(Boolean);
    if (additions.length === 0) return;
    if (additions.some(topic => topic.length > MAX_PATIENT_TOPIC_LENGTH)) {
      toast.error(`Тема: не более ${MAX_PATIENT_TOPIC_LENGTH} символов`);
      return;
    }
    const topics = normalizeTopics([...(form.topics || []), ...additions]);
    if (topics.length > MAX_PATIENT_TOPICS) {
      toast.error(`У клиента может быть не более ${MAX_PATIENT_TOPICS} тем`);
      return;
    }
    setForm(current => ({ ...current, topics }));
    setTopicInput('');
  };
  const removeTopicFromForm = (topicToRemove) => {
    const removeKey = normalizeKey(topicToRemove);
    setForm(current => ({
      ...current,
      topics: (current.topics || []).filter(topic => normalizeKey(topic) !== removeKey),
    }));
  };
  const deletePatient = async (id) => {
    try { await api.delete(`/patients/${id}`); toast.success('Клиент удалён'); fetchPatients(); }
    catch { toast.error('Ошибка удаления'); }
  };

  const openFilePicker = (patient) => {
    if (patient.sensitive_sections_locked) {
      api.get(`/patients/${patient.id}/files`).catch(() => {});
      openConsent(patient, true);
      return;
    }
    setUploadPatient(patient);
    requestAnimationFrame(() => fileInputRef.current?.click());
  };

  const uploadPatientFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !uploadPatient) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('note', '');
    try {
      await api.post(`/patients/${uploadPatient.id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} загружен`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Ошибка загрузки файла'));
    } finally {
      setUploadPatient(null);
    }
  };

  // Inline cell save
  const saveInlineField = async (patientId, colName, value) => {
    const patient = patients.find(p => p.id === patientId);
    if (!patient) return;
    const cf = { ...(patient.custom_fields || {}), [colName]: value };
    try {
      await api.put(`/patients/${patientId}`, { custom_fields: cf });
      setPatients(prev => prev.map(p => p.id === patientId ? { ...p, custom_fields: cf } : p));
    } catch { toast.error('Ошибка сохранения'); }
    setEditingCell(null);
  };

  // Column management
  const addColumn = async () => {
    const name = cleanColumnName(newColName);
    if (!name) return;
    if (columns.length >= MAX_CUSTOM_COLUMNS) { toast.error(`Можно добавить не более ${MAX_CUSTOM_COLUMNS} столбцов`); return; }
    if (name.length > MAX_COLUMN_NAME_LENGTH) { toast.error(`Название: не более ${MAX_COLUMN_NAME_LENGTH} символов`); return; }
    if (columns.some(c => normalizeKey(c.name) === normalizeKey(name))) { toast.error('Столбец уже существует'); return; }
    if (newColVisibility.length === 0) { toast.error('Выберите, кто видит столбец'); return; }
    const col = { name, type: newColType, visible_to: newColVisibility };
    if (newColType === 'select') {
      col.options = cleanOptions(newColOptions);
      if (col.options.length === 0) { toast.error('Добавьте хотя бы один вариант'); return; }
      if (col.options.length > MAX_SELECT_OPTIONS) { toast.error(`В списке может быть не более ${MAX_SELECT_OPTIONS} вариантов`); return; }
      if (col.options.some(option => option.length > MAX_SELECT_OPTION_LENGTH)) {
        toast.error(`Вариант: не более ${MAX_SELECT_OPTION_LENGTH} символов`);
        return;
      }
    }
    const ok = await saveColumns([...rawColumns, col]);
    if (ok) {
      setNewColName(''); setNewColType('text'); setNewColOptions(''); setNewColVisibility(defaultVisibility);
      setAddColumnOpen(false);
      toast.success(`Столбец «${name}» добавлен`);
    }
  };

  const removeColumn = async (colName) => {
    const ok = await saveColumns(rawColumns.filter(c => (typeof c === 'string' ? c : c.name) !== colName));
    if (ok) {
      const widthKey = customColumnWidthKey(colName);
      setColumnWidths(current => {
        if (!(widthKey in current)) return current;
        const next = { ...current };
        delete next[widthKey];
        return next;
      });
      toast.success(`Столбец «${colName}» удалён`);
    }
  };

  const saveEditCol = async () => {
    if (!editCol) return;
    const oldName = editCol.originalName || editCol.name;
    const name = cleanColumnName(editCol.name);
    if (!name) { toast.error('Введите название столбца'); return; }
    if (name.length > MAX_COLUMN_NAME_LENGTH) { toast.error(`Название: не более ${MAX_COLUMN_NAME_LENGTH} символов`); return; }
    if (columns.some(c => c.name !== oldName && normalizeKey(c.name) === normalizeKey(name))) {
      toast.error('Столбец уже существует');
      return;
    }
    if ((editCol.visible_to || []).length === 0) { toast.error('Выберите, кто видит столбец'); return; }
    const nextCol = { name, type: editCol.type, visible_to: editCol.visible_to || [] };
    if (editCol.type === 'select') {
      nextCol.options = cleanOptions((editCol.options || []).join(','));
      if (nextCol.options.length === 0) { toast.error('Добавьте хотя бы один вариант'); return; }
      if (nextCol.options.length > MAX_SELECT_OPTIONS) { toast.error(`В списке может быть не более ${MAX_SELECT_OPTIONS} вариантов`); return; }
      if (nextCol.options.some(option => option.length > MAX_SELECT_OPTION_LENGTH)) {
        toast.error(`Вариант: не более ${MAX_SELECT_OPTION_LENGTH} символов`);
        return;
      }
    }
    const updated = rawColumns.map(c => {
      const currentName = typeof c === 'string' ? c : c.name;
      return currentName === oldName ? nextCol : c;
    });
    const ok = await saveColumns(updated);
    if (!ok) return;
    if (oldName !== name) {
      const oldWidthKey = customColumnWidthKey(oldName);
      const newWidthKey = customColumnWidthKey(name);
      setColumnWidths(current => {
        if (!(oldWidthKey in current) || oldWidthKey === newWidthKey) return current;
        const next = { ...current, [newWidthKey]: current[oldWidthKey] };
        delete next[oldWidthKey];
        return next;
      });
      const patientsWithField = patients.filter(p => (p.custom_fields || {})[oldName] !== undefined);
      const results = await Promise.allSettled(patientsWithField.map(p => {
        const cf = { ...p.custom_fields, [name]: p.custom_fields[oldName] };
        delete cf[oldName];
        return api.put(`/patients/${p.id}`, { custom_fields: cf });
      }));
      if (results.some(result => result.status === 'rejected')) {
        toast.error('Столбец переименован, но часть значений перенести не удалось');
      }
      fetchPatients();
    }
    setEditColOpen(false);
    toast.success('Столбец обновлён');
  };

  // Balance & Prices (unchanged logic)
  const submitBalance = async () => {
    if (!balancePatient || !balanceAmount) return;
    try {
      await api.post(`/patients/${balancePatient.id}/${balanceType === 'deposit' ? 'deposit' : 'refund'}`, { amount: parseFloat(balanceAmount), note: balanceType === 'deposit' ? 'Пополнение' : 'Возврат' });
      toast.success(balanceType === 'deposit' ? 'Депозит пополнен' : 'Возврат оформлен');
      setBalanceOpen(false); setBalanceAmount(''); fetchPatients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };
  const openPrices = (p) => { setPricesPatient(p); setCustomPrices(p.custom_prices || {}); setPricesOpen(true); };
  const savePrices = async () => {
    if (!pricesPatient) return;
    const cleaned = {}; for (const [k, v] of Object.entries(customPrices)) { if (v !== '' && v != null) cleaned[k] = Number(v); }
    try { await api.put(`/patients/${pricesPatient.id}`, { custom_prices: cleaned }); toast.success('Цены сохранены'); setPricesOpen(false); fetchPatients(); }
    catch { toast.error('Ошибка'); }
  };

  const thCls = "text-stone-500 text-xs cursor-pointer select-none hover:text-stone-300 transition-colors whitespace-nowrap";
  const TypeIcon = ({ type }) => { const T = COL_TYPES.find(t => t.id === type)?.icon || Type; return <T size={10} className="text-stone-600" />; };

  // Render inline cell editor based on column type
  const renderCellEditor = (col, val, pId) => {
    const onSave = (v) => saveInlineField(pId, col.name, v);
    if (col.type === 'select') {
      return (
        <Select value={val || ''} onValueChange={v => onSave(v === '__empty__' ? '' : v)}>
          <SelectTrigger className="h-7 text-xs bg-stone-950 border-amber-700 text-stone-200 px-2 w-full">
            <SelectValue placeholder="Выберите..." />
          </SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-800">
            <SelectItem value="__empty__">— Очистить —</SelectItem>
            {(col.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === 'date') {
      return <Input type="date" autoFocus value={editingValue} onChange={e => setEditingValue(e.target.value)}
        onBlur={() => onSave(editingValue)} onKeyDown={e => { if (e.key === 'Enter') onSave(editingValue); if (e.key === 'Escape') setEditingCell(null); }}
        className="h-7 text-xs bg-stone-950 border-amber-700 text-stone-200 px-2" />;
    }
    if (col.type === 'number') {
      return <Input type="number" autoFocus value={editingValue} onChange={e => setEditingValue(e.target.value)}
        onBlur={() => onSave(editingValue)} onKeyDown={e => { if (e.key === 'Enter') onSave(editingValue); if (e.key === 'Escape') setEditingCell(null); }}
        className="h-7 text-xs bg-stone-950 border-amber-700 text-stone-200 px-2" />;
    }
    // text
    return <Input autoFocus value={editingValue} onChange={e => setEditingValue(e.target.value)}
      onBlur={() => onSave(editingValue)} onKeyDown={e => { if (e.key === 'Enter') onSave(editingValue); if (e.key === 'Escape') setEditingCell(null); }}
      className="h-7 text-xs bg-stone-950 border-amber-700 text-stone-200 px-2" />;
  };

  // Render cell display value
  const renderCellValue = (col, val) => {
    if (!val && val !== 0) return <span className="text-stone-700 italic">—</span>;
    if (col.type === 'date' && val) {
      const date = new Date(`${val}T00:00:00`);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
      }
      return String(val);
    }
    if (col.type === 'number') {
      const number = Number(val);
      return Number.isFinite(number) ? <span className="tabular-nums">{number.toLocaleString('ru-RU')}</span> : String(val);
    }
    if (col.type === 'select') {
      const colorMap = { 0: 'bg-sky-900/30 text-sky-400 border-sky-900/50', 1: 'bg-amber-900/30 text-amber-400 border-amber-900/50', 2: 'bg-green-900/30 text-green-400 border-green-900/50', 3: 'bg-red-900/30 text-red-400 border-red-900/50', 4: 'bg-violet-900/30 text-violet-400 border-violet-900/50' };
      const idx = (col.options || []).indexOf(val);
      const cls = colorMap[idx % 5] || colorMap[0];
      return <Badge title={String(val)} className={`${cls} border text-[10px] px-1.5 py-0 max-w-[180px] truncate`}>{val}</Badge>;
    }
    return val;
  };

  // Render form field based on column type
  const renderFormField = (col) => {
    const val = customFieldsForm[col.name] ?? '';
    const onChange = (v) => setCustomFieldsForm({ ...customFieldsForm, [col.name]: v });
    if (col.type === 'select') {
      return (
        <Select value={val} onValueChange={onChange}>
          <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm h-8">
            <SelectValue placeholder="Выберите..." />
          </SelectTrigger>
          <SelectContent className="bg-stone-900 border-stone-800">
            {(col.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === 'date') return <Input type="date" value={val} onChange={e => onChange(e.target.value)} className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm h-8" />;
    if (col.type === 'number') return <Input type="number" value={val} onChange={e => onChange(e.target.value)} className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm h-8" placeholder="0" />;
    return <Input value={val} maxLength={500} onChange={e => onChange(e.target.value)} className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm h-8" placeholder={`${col.name}...`} />;
  };

  return (
    <div data-testid="patients-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-full mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 animate-fade-in">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl lg:text-3xl text-stone-200">{t('clients') || 'Клиенты'}</h1>
              <Badge className="bg-stone-800 text-stone-400 border border-stone-700 text-xs">{patients.length}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {canManageColumns && (
                <Button data-testid="add-column-btn" size="sm" variant="outline"
                  disabled={columns.length >= MAX_CUSTOM_COLUMNS}
                  title={columns.length >= MAX_CUSTOM_COLUMNS ? `Достигнут лимит: ${MAX_CUSTOM_COLUMNS}` : `Добавлено ${columns.length} из ${MAX_CUSTOM_COLUMNS}`}
                  onClick={() => { setNewColName(''); setNewColType('text'); setNewColOptions(''); setNewColVisibility(defaultVisibility); setAddColumnOpen(true); }}
                  className="border-stone-700 text-stone-400 hover:text-stone-200 hover:border-stone-600 disabled:cursor-not-allowed">
                  <Plus size={14} className="mr-1" /> Столбец
                </Button>
              )}
              <Button data-testid="add-patient-btn" onClick={openAdd} className="bg-amber-700 hover:bg-amber-600 text-white">
                <Plus size={16} className="mr-1.5" /> Добавить
              </Button>
            </div>
          </div>

          <Card className="bg-stone-900 border-stone-800 mb-6 animate-fade-in delay-1">
            <CardContent className="p-4">
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-600" />
                  <Input data-testid="patient-search" aria-label="Поиск клиентов"
                    placeholder="Имя, контакты, тема или дополнительное поле" value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 pr-10 bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
                  {search && (
                    <button type="button" data-testid="clear-patient-search" aria-label="Очистить поиск"
                      onClick={() => setSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-600 hover:bg-stone-800 hover:text-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-700">
                      <X size={14} />
                    </button>
                  )}
                </div>
                <Select value={rankTopic ? `topic:${encodeURIComponent(rankTopic)}` : 'none'}
                  onValueChange={value => setRankTopic(value === 'none' ? '' : decodeURIComponent(value.slice(6)))}>
                  <SelectTrigger data-testid="patient-topic-ranking" aria-label="Ранжировать клиентов по теме"
                    className="border-stone-800 bg-stone-950/50 text-stone-300">
                    <span className="flex min-w-0 items-center gap-2">
                      <ListFilter size={15} className="shrink-0 text-stone-500" />
                      <SelectValue placeholder="Ранжировать по теме" />
                    </span>
                  </SelectTrigger>
                  <SelectContent className="max-h-72 border-stone-800 bg-stone-900 text-stone-300">
                    <SelectItem value="none">Без ранжирования по теме</SelectItem>
                    {topicStats.map(topic => (
                      <SelectItem key={normalizeKey(topic.name)} value={`topic:${encodeURIComponent(topic.name)}`}>
                        {topic.name} ({topic.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {rankTopic && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-stone-800 pt-3 text-xs text-stone-500">
                  <span>Сначала «{rankTopic}»: {rankedPatientCount} из {patients.length}</span>
                  <button type="button" onClick={() => setRankTopic('')}
                    className="rounded px-2 py-1 text-stone-400 hover:bg-stone-800 hover:text-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-700">
                    Сбросить
                  </button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-stone-900 border-stone-800 animate-fade-in delay-2">
            <CardContent className="p-0">
              {loading ? <p className="text-stone-500 text-center py-8">Загрузка...</p>
              : filtered.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={40} className="text-stone-700 mx-auto mb-3" />
                  <p className="text-stone-600 text-sm">{search ? 'Ничего не найдено' : 'Клиентов пока нет'}</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-800 px-3 py-2 text-[11px] text-stone-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Columns3 size={13} />
                      Потяните границу заголовка, чтобы изменить ширину
                    </span>
                    <span>
                      Двойной щелчок — сброс
                      {visibleColumns.length > 0 ? ` · Дополнительных столбцов: ${visibleColumns.length}` : ''}
                    </span>
                  </div>
                  <Table className="table-fixed" style={{ minWidth: `${patientTableMinWidth}px` }}>
                    <colgroup>
                      <col style={{ width: `${getColumnWidth('name')}px` }} />
                      <col style={{ width: `${getColumnWidth('topics')}px` }} />
                      <col style={{ width: `${getColumnWidth('phone')}px` }} />
                      <col style={{ width: `${getColumnWidth('balance')}px` }} />
                      {visibleColumns.map(col => <col key={col.name} style={{ width: `${getColumnWidth(customColumnWidthKey(col.name))}px` }} />)}
                      <col />
                      <col style={{ width: `${PATIENT_TABLE_COLUMN_CONFIG.actions.defaultWidth}px` }} />
                    </colgroup>
                    <TableHeader>
                      <TableRow className="border-stone-800 hover:bg-transparent">
                        <TableHead style={columnStyle('name')} className={`${thCls} relative sticky left-0 z-20 bg-stone-900`} onClick={() => toggleSort('name')}>
                          Имя <SortIcon col="name" />
                          {renderColumnResizeHandle('name', 'Имя')}
                        </TableHead>
                        <TableHead style={columnStyle('topics')} className="relative text-xs text-stone-500">
                          Темы
                          {renderColumnResizeHandle('topics', 'Темы')}
                        </TableHead>
                        <TableHead style={columnStyle('phone')} className={`${thCls} relative`} onClick={() => toggleSort('phone')}>
                          Телефон <SortIcon col="phone" />
                          {renderColumnResizeHandle('phone', 'Телефон')}
                        </TableHead>
                        <TableHead style={columnStyle('balance')} className={`${thCls} relative`} onClick={() => toggleSort('balance')}>
                          Баланс <SortIcon col="balance" />
                          {renderColumnResizeHandle('balance', 'Баланс')}
                        </TableHead>
                        {visibleColumns.map(col => (
                          <TableHead key={col.name} style={columnStyle(customColumnWidthKey(col.name))} className={`${thCls} relative`} onClick={() => toggleSort(col.name)}>
                            <span className="group flex min-w-0 items-center gap-1 overflow-hidden pr-1">
                              <TypeIcon type={col.type} />
                              <span title={col.name} className="min-w-0 flex-1 truncate">{col.name}</span>
                              <SortIcon col={col.name} />
                              {canManageColumns && (
                                <>
                                  <button type="button" aria-label={`Настроить столбец «${col.name}»`}
                                    onClick={(e) => { e.stopPropagation(); setEditCol({ ...col, originalName: col.name }); setEditColOpen(true); }}
                                    className="rounded p-0.5 text-stone-600 opacity-60 transition-opacity hover:text-amber-500 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-amber-700 sm:opacity-0 sm:group-hover:opacity-100" title="Настроить столбец">
                                    <Pencil size={11} />
                                  </button>
                                  <button type="button" aria-label={`Удалить столбец «${col.name}»`}
                                    onClick={(e) => { e.stopPropagation(); setColumnToDelete(col); }}
                                    className="rounded p-0.5 text-stone-600 opacity-60 transition-opacity hover:text-red-400 focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-red-700 sm:opacity-0 sm:group-hover:opacity-100" title="Удалить столбец">
                                    <X size={11} />
                                  </button>
                                </>
                              )}
                            </span>
                            {renderColumnResizeHandle(customColumnWidthKey(col.name), col.name)}
                          </TableHead>
                        ))}
                        <TableHead aria-hidden="true" className="p-0" />
                        <TableHead style={columnStyle('actions')} className="sticky right-0 z-20 bg-stone-900 text-center text-xs text-stone-500">
                          Действия
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map(p => {
                        const patientTopics = getPatientTopics(p);
                        return (
                        <TableRow key={p.id} data-testid={`patient-row-${p.id}`} className="group border-stone-800/50 hover:bg-stone-800/30">
                          <TableCell title={p.name || 'Без имени'} style={columnStyle('name')}
                            className="sticky left-0 z-10 overflow-hidden bg-stone-900 text-sm font-medium text-stone-200 group-hover:bg-stone-800">
                            <div className="truncate">{p.name || 'Без имени'}</div>
                            {consentContourEnabled && <button type="button" onClick={() => openConsent(p, p.sensitive_sections_locked)}
                              className={`mt-1 inline-flex items-center gap-1 rounded text-[10px] font-normal focus:outline-none focus:ring-1 focus:ring-amber-700 ${p.sensitive_sections_locked ? 'text-amber-500' : 'text-emerald-500'}`}>
                              {p.sensitive_sections_locked ? <ShieldAlert size={11} /> : <ShieldCheck size={11} />}
                              {p.sensitive_sections_locked ? 'Согласие не подтверждено' : 'Согласие получено'}
                            </button>}
                          </TableCell>
                          <TableCell style={columnStyle('topics')} className="overflow-hidden">
                            {patientTopics.length > 0 ? (
                              <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                                {patientTopics.slice(0, 2).map(topic => {
                                  const isRanked = rankTopic && normalizeKey(topic) === normalizeKey(rankTopic);
                                  return (
                                    <Badge key={normalizeKey(topic)} title={topic}
                                      className={`max-w-[88px] truncate border text-[10px] font-medium ${isRanked ? 'border-amber-700/60 bg-amber-900/30 text-amber-400' : 'border-stone-700 bg-stone-800 text-stone-400'}`}>
                                      {topic}
                                    </Badge>
                                  );
                                })}
                                {patientTopics.length > 2 && (
                                  <span title={patientTopics.slice(2).join(', ')} className="shrink-0 text-[10px] text-stone-600">
                                    +{patientTopics.length - 2}
                                  </span>
                                )}
                              </div>
                            ) : <span className="text-xs text-stone-700">Без тем</span>}
                          </TableCell>
                          <TableCell style={columnStyle('phone')} className="overflow-hidden whitespace-nowrap text-sm text-stone-400">
                            {p.phone
                              ? <span className="flex min-w-0 items-center gap-1.5"><Phone size={12} className="shrink-0 text-stone-600" /><span className="truncate">{p.phone}</span></span>
                              : <span className="text-stone-600">—</span>}
                          </TableCell>
                          <TableCell style={columnStyle('balance')} className="overflow-hidden">
                            <span className={`text-sm font-medium ${(p.balance || 0) > 0 ? 'text-green-400' : (p.balance || 0) < 0 ? 'text-red-400' : 'text-stone-600'}`}>
                              {(p.balance || 0) !== 0 ? formatMoney(p.balance || 0, currency) : '—'}
                            </span>
                          </TableCell>
                          {visibleColumns.map(col => {
                            const cellKey = `${p.id}_${col.name}`;
                            const val = (p.custom_fields || {})[col.name] ?? '';
                            const isEditing = editingCell === cellKey;
                            return (
                              <TableCell key={col.name} style={columnStyle(customColumnWidthKey(col.name))} className="overflow-hidden text-sm">
                                {isEditing ? renderCellEditor(col, val, p.id) : (
                                  <button type="button" data-testid={`cell-${p.id}-${col.name}`}
                                    title={val === '' ? 'Нажмите, чтобы заполнить' : String(val)}
                                    className="block w-full truncate rounded px-1 py-0.5 text-left text-stone-400 transition-colors hover:bg-stone-800/50 hover:text-stone-200 focus:outline-none focus:ring-2 focus:ring-amber-700"
                                    onClick={() => { setEditingCell(cellKey); setEditingValue(val); }}>
                                    {renderCellValue(col, val)}
                                  </button>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell aria-hidden="true" className="p-0" />
                          <TableCell style={columnStyle('actions')} className="sticky right-0 z-10 bg-stone-900 p-1 text-right group-hover:bg-stone-800">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button data-testid={`patient-actions-${p.id}`} variant="ghost" size="sm"
                                  aria-label={`Действия с клиентом ${p.name || ''}`}
                                  className="h-8 w-full justify-center gap-1.5 px-2 text-xs font-medium text-stone-400 hover:text-stone-100">
                                  <MoreHorizontal size={15} />
                                  <span>Действия</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52 border-stone-700 bg-stone-900 text-stone-300">
                                <DropdownMenuItem data-testid={`edit-patient-${p.id}`} onSelect={() => openEdit(p)} className="focus:bg-stone-800 focus:text-stone-100">
                                  <Pencil /> Редактировать
                                </DropdownMenuItem>
                                {consentContourEnabled && <DropdownMenuItem onSelect={() => openConsent(p, p.sensitive_sections_locked)} className="focus:bg-stone-800 focus:text-stone-100">
                                  {p.sensitive_sections_locked ? <ShieldAlert /> : <ShieldCheck />} Согласие
                                </DropdownMenuItem>}
                                <DropdownMenuItem data-testid={`history-patient-${p.id}`} onSelect={() => { setHistoryPatient(p); setHistoryOpen(true); }} className="focus:bg-stone-800 focus:text-stone-100">
                                  <History /> История
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => navigate(`/patients/${p.id}/board`)} className="focus:bg-stone-800 focus:text-stone-100">
                                  <LayoutDashboard /> Дашборд
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-stone-800" />
                                <DropdownMenuItem data-testid={`prices-patient-${p.id}`} onSelect={() => openPrices(p)} className="focus:bg-stone-800 focus:text-stone-100">
                                  <Tag /> Индивидуальные цены
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setNotifyPatient(p); setNotifyForm({ subject: '', message: '' }); setNotifyOpen(true); }} className="focus:bg-stone-800 focus:text-stone-100">
                                  <Bell /> Уведомить
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => openFilePicker(p)} className="focus:bg-stone-800 focus:text-stone-100">
                                  <Paperclip /> Прикрепить файл
                                </DropdownMenuItem>
                                <DropdownMenuItem data-testid={`deposit-patient-${p.id}`} onSelect={() => { setBalancePatient(p); setBalanceType('deposit'); setBalanceOpen(true); }} className="focus:bg-stone-800 focus:text-stone-100">
                                  <ArrowDownCircle /> Пополнить депозит
                                </DropdownMenuItem>
                                {(p.balance || 0) > 0 && (
                                  <DropdownMenuItem data-testid={`refund-patient-${p.id}`} onSelect={() => { setBalancePatient(p); setBalanceType('refund'); setBalanceOpen(true); }} className="focus:bg-stone-800 focus:text-stone-100">
                                    <ArrowUpCircle /> Оформить возврат
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-stone-800" />
                                <DropdownMenuItem data-testid={`delete-patient-${p.id}`} onSelect={() => setPatientToDelete(p)} className="text-red-400 focus:bg-red-950/40 focus:text-red-300">
                                  <Trash2 /> Удалить клиента
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <input ref={fileInputRef} type="file" className="hidden" onChange={uploadPatientFile} />

      {/* Add/Edit Patient Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-xl grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden border-stone-800 bg-stone-900 p-0 text-stone-200">
          <DialogHeader className="px-6 pb-4 pt-6">
            <DialogTitle className="font-heading text-stone-200">{editId ? 'Редактировать клиента' : 'Новый клиент'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto px-6 pb-5">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-stone-400">Имя <span className="text-red-400">*</span></label>
              <Input data-testid="patient-form-name" value={form.name} maxLength={160} onChange={e => setForm({ ...form, name: e.target.value })}
                className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="ФИО клиента" />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className="mb-1.5 block text-xs font-medium text-stone-400">Телефон</label>
                <Input data-testid="patient-form-phone" value={form.phone} maxLength={40} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="+7 (999) 123-45-67" /></div>
              <div><label className="mb-1.5 block text-xs font-medium text-stone-400">Email</label>
                <Input data-testid="patient-form-email" type="email" value={form.email} maxLength={254} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="email@example.com" /></div>
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <label htmlFor="patient-topic-input" className="text-xs font-medium text-stone-400">Темы</label>
                <span className="text-[11px] text-stone-600">{(form.topics || []).length}/{MAX_PATIENT_TOPICS}</span>
              </div>
              <div className="flex gap-2">
                <Input id="patient-topic-input" data-testid="patient-topic-input" value={topicInput}
                  maxLength={MAX_PATIENT_TOPIC_LENGTH}
                  onChange={event => setTopicInput(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ',') {
                      event.preventDefault();
                      addTopicsFromInput();
                    }
                  }}
                  className="bg-stone-950/50 border-stone-800 text-stone-200"
                  placeholder="Например, тревога или отношения" />
                <Button type="button" variant="outline" onClick={addTopicsFromInput}
                  disabled={!topicInput.trim() || (form.topics || []).length >= MAX_PATIENT_TOPICS}
                  className="shrink-0 border-stone-700 text-stone-300 hover:bg-stone-800 hover:text-stone-100">
                  Добавить
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-stone-600">Enter или запятая позволяют добавить несколько тем.</p>
              {(form.topics || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Темы клиента">
                  {(form.topics || []).map(topic => (
                    <Badge key={normalizeKey(topic)} className="gap-1 border border-stone-700 bg-stone-800 pr-1 text-xs font-medium text-stone-300">
                      <Tag size={11} aria-hidden="true" />
                      <span className="max-w-[180px] truncate" title={topic}>{topic}</span>
                      <button type="button" onClick={() => removeTopicFromForm(topic)} aria-label={`Удалить тему «${topic}»`}
                        className="rounded p-0.5 text-stone-500 hover:bg-stone-700 hover:text-stone-200 focus:outline-none focus:ring-1 focus:ring-amber-700">
                        <X size={11} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div><label className="mb-1.5 block text-xs font-medium text-stone-400">Профессиональные заметки</label>
              {consentContourEnabled && (!editId || patients.find(patient => patient.id === editId)?.sensitive_sections_locked) ? (
                <div className="rounded-lg border border-amber-900/50 bg-amber-950/20 p-3 text-xs text-amber-200">
                  Для хранения чувствительной информации необходимо подтвердить наличие соответствующего согласия клиента
                  {editId && <Button type="button" variant="ghost" onClick={() => openConsent(patients.find(patient => patient.id === editId), true)} className="mt-2 h-8 w-full text-amber-300 hover:bg-amber-950/40">Подтвердить согласие</Button>}
                </div>
              ) : (
                <Textarea data-testid="patient-form-notes" value={form.notes} maxLength={2000} rows={2} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="resize-none bg-stone-950/50 border-stone-800 text-stone-200" placeholder="Заметки о клиенте" />
              )}</div>
            {visibleColumns.length > 0 && (
              <div className="border-t border-stone-800 pt-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-xs font-medium text-stone-400">Дополнительные поля</p>
                  <span className="text-[11px] text-stone-600">{visibleColumns.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {visibleColumns.map(col => (
                    <div key={col.name} className="min-w-0">
                      <label title={col.name} className="mb-1.5 flex items-center gap-1 truncate text-xs text-stone-400">
                        <TypeIcon type={col.type} />{col.name}
                      </label>
                      {renderFormField(col)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-stone-800 px-6 py-4">
            <Button variant="secondary" onClick={() => setDialogOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="patient-form-save" onClick={savePatient} disabled={saving} className="bg-amber-700 hover:bg-amber-600 text-white">
              {saving ? 'Сохранение...' : 'Сохранить'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PatientHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} patient={historyPatient} />
      {consentContourEnabled && <PatientConsentDialog open={consentOpen} onOpenChange={setConsentOpen} patient={consentPatient}
        gate={consentGate} canManageTemplate={isOwner || isSoloDoctor} onUpdated={fetchPatients} />
      }

      {/* Balance Dialog */}
      <Dialog open={balanceOpen} onOpenChange={setBalanceOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">{balanceType === 'deposit' ? 'Пополнить депозит' : 'Оформить возврат'}</DialogTitle></DialogHeader>
          {balancePatient && (<div className="space-y-4">
            <p className="text-stone-400 text-sm">{balancePatient.name}</p>
            <p className="text-stone-500 text-xs">Баланс: {formatMoney(balancePatient.balance || 0, currency)}</p>
            <Input data-testid="balance-amount" type="number" value={balanceAmount} onChange={e => setBalanceAmount(e.target.value)}
              className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="5000" />
          </div>)}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setBalanceOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="balance-submit" onClick={submitBalance}
              className={balanceType === 'deposit' ? 'bg-green-800 hover:bg-green-700 text-white' : 'bg-red-800 hover:bg-red-700 text-white'}>
              {balanceType === 'deposit' ? 'Пополнить' : 'Вернуть'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Prices Dialog */}
      <Dialog open={pricesOpen} onOpenChange={setPricesOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Индивидуальные цены</DialogTitle></DialogHeader>
          {pricesPatient && (<div className="space-y-4">
            <p className="text-stone-400 text-sm">{pricesPatient.name}</p>
            <p className="text-stone-600 text-xs">Оставьте пустым для стандартной цены</p>
            {formats.map(fmt => (
              <div key={fmt.id} className="flex items-center gap-3">
                <label className="text-stone-300 text-sm flex-1">{fmt.name} <span className="text-stone-600">({formatMoney(fmt.price || 0, fmt.currency || currency)})</span></label>
                <Input data-testid={`custom-price-${fmt.id}`} type="number" value={customPrices[fmt.id] ?? ''}
                  onChange={e => setCustomPrices({ ...customPrices, [fmt.id]: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200 w-32" placeholder={String(fmt.price || 0)} />
              </div>))}
          </div>)}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPricesOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="save-prices-btn" onClick={savePrices} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Column Dialog */}
      <Dialog open={addColumnOpen} onOpenChange={setAddColumnOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-sm overflow-y-auto border-stone-800 bg-stone-900 text-stone-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Новый столбец</DialogTitle>
            <p className="text-xs text-stone-500">Добавлено {columns.length} из {MAX_CUSTOM_COLUMNS}</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-stone-400 text-xs font-medium mb-1.5 block">Название</label>
              <Input data-testid="new-column-name" autoFocus value={newColName} maxLength={MAX_COLUMN_NAME_LENGTH} onChange={e => setNewColName(e.target.value)}
                placeholder="Источник, Диагноз, Дата рождения..." className="bg-stone-950/50 border-stone-800 text-stone-200" />
              <p className="mt-1 text-right text-[10px] text-stone-600">{newColName.length}/{MAX_COLUMN_NAME_LENGTH}</p>
            </div>
            <div>
              <label className="text-stone-400 text-xs font-medium mb-2 block">Тип столбца</label>
              <div className="grid grid-cols-2 gap-2">
                {COL_TYPES.map(t => (
                  <button key={t.id} data-testid={`col-type-${t.id}`} onClick={() => setNewColType(t.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg text-xs border transition-all ${
                      newColType === t.id ? 'bg-amber-700/15 border-amber-700/50 text-amber-500' : 'bg-stone-950/50 border-stone-800 text-stone-500 hover:border-stone-700'
                    }`}>
                    <t.icon size={14} />{t.label}
                  </button>
                ))}
              </div>
            </div>
            {newColType === 'select' && (
              <div>
                <label className="text-stone-400 text-xs font-medium mb-1.5 block">Варианты (через запятую)</label>
                <Input data-testid="col-options-input" value={newColOptions}
                  maxLength={MAX_SELECT_OPTIONS * (MAX_SELECT_OPTION_LENGTH + 1)}
                  onChange={e => setNewColOptions(e.target.value)}
                  placeholder="В работе, Завершён, Отказ" className="bg-stone-950/50 border-stone-800 text-stone-200" />
                <p className="mt-1 text-right text-[10px] text-stone-600">{cleanOptions(newColOptions).length}/{MAX_SELECT_OPTIONS} вариантов</p>
                {newColOptions && (
                  <div className="mt-2 flex max-h-24 flex-wrap gap-1 overflow-y-auto">
                    {cleanOptions(newColOptions).slice(0, MAX_SELECT_OPTIONS).map((opt, i) => (
                      <Badge key={i} title={opt} className="max-w-full truncate bg-stone-800 text-[10px] text-stone-300 border-stone-700">{opt}</Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Visibility */}
            <div>
              <label className="text-stone-400 text-xs font-medium mb-2 block">Кто видит столбец</label>
              <div className="flex gap-3">
                {visibilityRoles.map(r => (
                  <label key={r.id} className="flex items-center gap-1.5 cursor-pointer text-xs text-stone-300">
                    <input type="checkbox" checked={newColVisibility.includes(r.id)}
                      onChange={e => {
                        if (e.target.checked) setNewColVisibility(v => [...v, r.id]);
                        else setNewColVisibility(v => v.filter(x => x !== r.id));
                      }}
                      className="rounded border-stone-700 bg-stone-950" />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddColumnOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="confirm-add-column" onClick={addColumn} className="bg-amber-700 hover:bg-amber-600 text-white">Добавить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Column Dialog (type/options) */}
      <Dialog open={editColOpen} onOpenChange={setEditColOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-sm overflow-y-auto border-stone-800 bg-stone-900 text-stone-200">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Настроить столбец</DialogTitle></DialogHeader>
          {editCol && (
            <div className="space-y-4">
              <div>
                <label className="text-stone-400 text-xs font-medium mb-1.5 block">Название</label>
                <Input value={editCol.name} maxLength={MAX_COLUMN_NAME_LENGTH} onChange={e => setEditCol({ ...editCol, name: e.target.value })}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" />
                <p className="mt-1 text-right text-[10px] text-stone-600">{editCol.name.length}/{MAX_COLUMN_NAME_LENGTH}</p>
              </div>
              <div>
                <label className="text-stone-400 text-xs font-medium mb-2 block">Тип</label>
                <div className="grid grid-cols-2 gap-2">
                  {COL_TYPES.map(t => (
                    <button key={t.id} onClick={() => setEditCol({ ...editCol, type: t.id })}
                      className={`flex items-center gap-2 p-2.5 rounded-lg text-xs border transition-all ${
                        editCol.type === t.id ? 'bg-amber-700/15 border-amber-700/50 text-amber-500' : 'bg-stone-950/50 border-stone-800 text-stone-500 hover:border-stone-700'
                      }`}><t.icon size={14} />{t.label}</button>
                  ))}
                </div>
              </div>
              {editCol.type === 'select' && (
                <div>
                  <label className="text-stone-400 text-xs font-medium mb-1.5 block">Варианты (через запятую)</label>
                  <Input value={(editCol.options || []).join(', ')}
                    maxLength={MAX_SELECT_OPTIONS * (MAX_SELECT_OPTION_LENGTH + 1)}
                    onChange={e => setEditCol({ ...editCol, options: cleanOptions(e.target.value) })}
                    className="bg-stone-950/50 border-stone-800 text-stone-200" />
                  <p className="mt-1 text-right text-[10px] text-stone-600">{(editCol.options || []).length}/{MAX_SELECT_OPTIONS} вариантов</p>
                  <div className="mt-2 flex max-h-24 flex-wrap gap-1 overflow-y-auto">
                    {(editCol.options || []).slice(0, MAX_SELECT_OPTIONS).map((opt, i) => <Badge key={i} title={opt} className="max-w-full truncate bg-stone-800 text-[10px] text-stone-300 border-stone-700">{opt}</Badge>)}
                  </div>
                </div>
              )}
              <div>
                <label className="text-stone-400 text-xs font-medium mb-2 block">Видимость</label>
                <div className="flex gap-3">
                  {visibilityRoles.map(r => (
                    <label key={r.id} className="flex items-center gap-1.5 cursor-pointer text-xs text-stone-300">
                      <input type="checkbox" checked={(editCol.visible_to || []).includes(r.id)}
                        onChange={e => {
                          const vt = editCol.visible_to || [];
                          setEditCol({ ...editCol, visible_to: e.target.checked ? [...vt, r.id] : vt.filter(x => x !== r.id) });
                        }}
                        className="rounded border-stone-700 bg-stone-950" />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditColOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button onClick={saveEditCol} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Send Notification Dialog */}
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Уведомить клиента</DialogTitle></DialogHeader>
          {notifyPatient && (
            <div className="space-y-3">
              <p className="text-stone-400 text-xs">{notifyPatient.name}{notifyPatient.phone ? ` · ${notifyPatient.phone}` : ''}</p>
              <p className="text-stone-600 text-[10px]">
                Приоритет: {(notifyPatient.notification_preferences?.priority || ['telegram','email','sms']).map(c => c === 'telegram' ? 'Telegram' : c === 'email' ? 'Email' : 'SMS').join(' → ')}
              </p>
              <div>
                <label className="text-stone-500 text-[10px] mb-1 block">Тема</label>
                <Input value={notifyForm.subject} onChange={e => setNotifyForm(f => ({...f, subject: e.target.value}))}
                  placeholder="Тема уведомления" className="bg-stone-950/50 border-stone-800 text-stone-200" />
              </div>
              <div>
                <label className="text-stone-500 text-[10px] mb-1 block">Сообщение</label>
                <Textarea value={notifyForm.message} onChange={e => setNotifyForm(f => ({...f, message: e.target.value}))}
                  placeholder="Текст уведомления..." rows={3} className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm resize-none" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setNotifyOpen(false)} className="bg-stone-800 text-stone-300">Отмена</Button>
            <Button onClick={async () => {
              if (!notifyForm.subject || !notifyForm.message) { toast.error('Заполните тему и сообщение'); return; }
              try {
                const { data } = await api.post(`/patients/${notifyPatient.id}/notify`, notifyForm);
                toast.success(`Отправлено через ${data.channel === 'telegram' ? 'Telegram' : data.channel === 'email' ? 'Email' : data.channel}`);
                setNotifyOpen(false);
              } catch { toast.error('Ошибка отправки'); }
            }} className="bg-sky-700 hover:bg-sky-600 text-white">
              <Bell size={14} className="mr-1" /> Отправить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!columnToDelete} onOpenChange={open => { if (!open) setColumnToDelete(null); }}>
        <AlertDialogContent className="border-stone-800 bg-stone-900 text-stone-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить столбец «{columnToDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Столбец исчезнет из таблицы и форм. Уже введённые значения останутся в карточках клиентов и вернутся, если создать столбец с тем же названием.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-stone-700 bg-stone-800 text-stone-300 hover:bg-stone-700 hover:text-stone-100">Отмена</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-column"
              onClick={() => { const name = columnToDelete?.name; setColumnToDelete(null); if (name) removeColumn(name); }}
              className="bg-red-800 text-white hover:bg-red-700">Удалить столбец</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!patientToDelete} onOpenChange={open => { if (!open) setPatientToDelete(null); }}>
        <AlertDialogContent className="border-stone-800 bg-stone-900 text-stone-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить клиента «{patientToDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Карточка клиента будет удалена. Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-stone-700 bg-stone-800 text-stone-300 hover:bg-stone-700 hover:text-stone-100">Отмена</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-patient"
              onClick={() => { const id = patientToDelete?.id; setPatientToDelete(null); if (id) deletePatient(id); }}
              className="bg-red-800 text-white hover:bg-red-700">Удалить клиента</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
